// Pojedyncza próba ładowania skryptu. Dłuższe niż realne wolne łącze, żeby nie ubijać
// legalnie wolnych pobrań - chodzi tylko o zamianę "wiszenia w nieskończoność" na błąd.
const SCRIPT_TIMEOUT_MS = 30000;
// Ile razy ponowić po nieudanej próbie (samonaprawa transientów: recykl app-poola, 503, blip).
const SCRIPT_RETRIES = 2;
// Bazowy odstęp między próbami (rośnie liniowo: 800 ms, 1600 ms, ...).
const SCRIPT_RETRY_BACKOFF_MS = 800;

/**
 * Adds a new script tag to document head with the provided src.
 * Z timeoutem (utknięte żądanie nie wisi w nieskończoność) i ponawianiem (transient failures).
 */
export function AddScript(src: string, nonce: string | null): Promise<void> {
    return loadScriptWithRetry(src, nonce, 0);
}

function loadScriptWithRetry(src: string, nonce: string | null, attempt: number): Promise<void> {
    return loadScriptOnce(src, nonce, attempt).catch(error => {
        if (attempt >= SCRIPT_RETRIES)
            throw error;

        console.warn(`Ładowanie skryptu ${src} nie powiodło się (próba ${attempt + 1}/${SCRIPT_RETRIES + 1}), ponawiam...`, error);
        return new Promise<void>(resolve => setTimeout(resolve, SCRIPT_RETRY_BACKOFF_MS * (attempt + 1)))
            .then(() => loadScriptWithRetry(src, nonce, attempt + 1));
    });
}

function loadScriptOnce(src: string, nonce: string | null, attempt: number): Promise<void> {
    const script = document.createElement("script") as HTMLScriptElement;
    // Na ponowieniach dokładamy cache-bust, żeby ominąć ewentualną negatywną odpowiedź z cache.
    script.src = attempt === 0 ? src : src + (src.includes("?") ? "&" : "?") + "_retry=" + attempt;

    if (nonce)
        script.setAttribute("nonce", nonce);

    return new Promise<void>((resolve, reject) => {
        let timer: number;

        const cleanup = () => {
            clearTimeout(timer);
            script.onload = null;
            script.onerror = null;
        };

        script.onload = () => {
            cleanup();
            resolve();
        };
        script.onerror = () => {
            cleanup();
            script.remove();
            reject(new Error(`Błąd ładowania skryptu: ${script.src}`));
        };
        timer = window.setTimeout(() => {
            cleanup();
            script.remove();
            reject(new Error(`Timeout (${SCRIPT_TIMEOUT_MS} ms) ładowania skryptu: ${script.src}`));
        }, SCRIPT_TIMEOUT_MS);

        document.head.appendChild(script);
    });
}

/**
 * Adds a new link tag to document head with the provided href and rel=stylesheet.
 */
export function AddStyle(href: string, nonce: string | null): HTMLLinkElement {
    const linkTag = document.createElement("link") as HTMLLinkElement;
    linkTag.href = href;
    linkTag.rel = "stylesheet";

    if (nonce)
        linkTag.setAttribute("nonce", nonce);

    document.head.appendChild(linkTag);

    return linkTag;
}

/**
 * Adds html to the head tag.
 */
export function AddHeadHtml(html: string): void {
    document.head.insertAdjacentHTML("afterbegin", html);
}

/**
 * Helper function for checking the location's pathname.
 * @param pages Pass the page you want to check for without a slash (case sensitive).
 * @param appBase TT.AppBase.
 * @returns True if it matches one of the provided strings.
 */
export function IsOnPage(pages: string | string[], appBase: string): boolean {
    if (!Array.isArray(pages))
        pages = [pages];

    for (const page of pages)
        if (location.pathname === appBase + page)
            return true;

    return false;
}

/**
 * Removes the main spinner that's hardcoded in the wwwroot/*.html files.
 */
export function DisableMainSpinner(): void {
    const spinnerContainer = document.getElementsByClassName('MainSpinnerContainer')[0];
    if (!spinnerContainer)
        return;

    spinnerContainer.classList.add('MainSpinnerFadeOut');
}

/**
 * Sets the top level message to the provided text and color.
 */
export function SetTopLevelMessage(color: string, text: string): void {
    const topLevelMessage = document.getElementById("TtTopLevelMessage");
    topLevelMessage.innerText = text;
    topLevelMessage.style.color = color;
    topLevelMessage.style.opacity = "1";
}
